import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Dayjs } from 'dayjs';
import classNames from 'classnames';
import { SOW_ACTIVE_STATUSES, canDownloadConfirmation } from '../../utils';
import { useUser } from '../../hooks/useUser';
import { downloadConfirmationDetails } from '../../utils/confirmationDetailsExport';
import { BrandProject } from '../../pages/videoProduction/components/constants';
import { ExpertCalendar } from './ExpertCalendar';
import { Button } from '../Button/Button';
import { getProjectPros } from '../../api/endpoints';
import {
  hasFilmWindow,
  isWithinFilmWindow,
  formatFilmWindow,
  formatFilmDayList,
  getFilmDays,
  getFilmDaysCount,
  toDay,
  toDayList,
  FILM_DAYS_ERRORS,
} from '../../utils/filmWindow';
import axiosInstant from '../../api';
import { adminConfirmationPath, BRAND_PROJECT_STATUSES, describeSharedSiblings, expertDisplayName, getBpp, getProDecision, isProOptedIn } from '../../pages/videoProduction/helper';
import { ConfirmStepNotice, ConfirmStepNoticeProps } from './ConfirmStepNotice';
import { useProcessSiblings } from '../../hooks/useProcessSiblings';
import { useSelectionFilmWindow } from '../../hooks/useSelectionFilmWindow';
import { ExpertConfirmCard } from '../../pages/videoProduction/components/ExpertConfirmCard';

const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 1, color: 'var(--text-2)', textTransform: 'uppercase' as const };

interface IFilmingScheduling {
  project: BrandProject;
  onClose: () => void;
  onComplete: () => void;
}

export const FilmingScheduling: FC<IFilmingScheduling> = ({ project, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { role } = useUser();
  const brandId = project?.brands?.filter((brand: any) => !brand.rmn)?.[0]?.id;
  const hasExperts = project?.experts !== false;

  // Expert confirmation is shared across the selection process, so confirming
  // here confirms for every sibling project. Confirming an expert SENDS their
  // SOW; the WHOLE selection moves itself to Pre-Production once every
  // confirmed expert's SOW is signed and countersigned (projects that share
  // an expert selection share one shoot date). Only an expert-less project is
  // moved by this dialog's Save.
  const sharedProcess = useProcessSiblings(
    project?.order_id != null ? Number(project.order_id) : undefined,
    project?.id,
    { siblingCount: project?.process_project_count }
  );
  const otherProcessProjects = sharedProcess.siblingProjects.filter(
    (p) => Number(p.id) !== Number(project?.id)
  );
  const [experts, setExperts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [filmDays, setFilmDays] = useState(1);
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const selectionId =
    sharedProcess.processId != null
      ? sharedProcess.processId
      : (project as any)?.brand_order_pros_id ?? null;

  const { filmWindow } = useSelectionFilmWindow(
    project?.order_id != null ? Number(project.order_id) : null,
    selectionId
  );
  const [showRescheduleWarning, setShowRescheduleWarning] = useState(false);
  const [pendingUnconfirm, setPendingUnconfirm] = useState<any | null>(null);
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);
  // "SOW already signed" ticks per expert, held until their confirm goes out.
  const [alreadySignedByUser, setAlreadySignedByUser] = useState<Record<number, boolean>>({});

  const savedDays = useMemo(() => getFilmDays(filmWindow), [filmWindow]);
  const savedDayCount = getFilmDaysCount(filmWindow);

  useEffect(() => {
    if (!filmWindow || scheduleTouched) return;
    setSelectedDays(savedDays);
    setFilmDays(savedDayCount);
  }, [filmWindow, scheduleTouched, savedDays, savedDayCount]);

  const scheduleComplete =
    selectedDays.length === filmDays && selectedDays.length > 0;
  // Only experts who opted in can be scheduled (TDC-1633): they are the only
  // ones the API asks to confirm the date, so the confirm list, its counts
  // and the notices below all work from this subset. `experts` stays the
  // whole roster for the reschedule warnings (a stale confirm or live SOW on
  // anyone still has to be voided).
  const optedInExperts = useMemo(() => experts.filter(isProOptedIn), [experts]);
  const hasOptedOut = (expert: any) =>
    getProDecision(getBpp(expert)) === 'opted_out';
  const hasConfirmedDate = (expert: any) =>
    !hasOptedOut(expert) && expert.user_confirmed === true;
  // Mirrors the API's SOW gate: an expert counts only when the team
  // confirmed them on the project AND they confirmed the film date.
  const confirmedExperts = optedInExperts.filter(
    (e) => e.internal_confirmed && hasConfirmedDate(e)
  );
  const confirmedCount = confirmedExperts.length;
  const isSowExecuted = (e: any) =>
    e.expert_signing_status === 'completed' && e.internal_signing_status === 'completed';
  const executedCount = confirmedExperts.filter(isSowExecuted).length;
  const awaitingCountersign = confirmedExperts.filter(
    (e) => e.expert_signing_status === 'completed' && e.internal_signing_status !== 'completed'
  );
  const anySowLive = experts.some((e) =>
    SOW_ACTIVE_STATUSES.includes(e.expert_signing_status || '')
  );
  const anyInternallyConfirmed = experts.some((e) => !!e.internal_confirmed);

  const toggleDay = (dayKey: string) => {
    setScheduleTouched(true);
    setSelectedDays((prev) => {
      if (prev.includes(dayKey)) return prev.filter((day) => day !== dayKey);
      // Single-day mode: clicking a new day REPLACES the selection (standard
      // date-picker semantics). Without this — paired with the calendar not
      // freezing at cap in single-day mode — picking the wrong day left no
      // visible way to change it.
      if (filmDays === 1) return [dayKey];
      return [...prev, dayKey].sort();
    });
  };

  const changeFilmDays = (next: number) => {
    const count = Math.max(1, Math.min(30, Math.floor(next) || 1));
    setScheduleTouched(true);
    setFilmDays(count);
    setSelectedDays((prev) => (prev.length > count ? prev.slice(0, count) : prev));
  };

  const preferredDaysByExpert = optedInExperts.reduce(
    (acc: Record<string, string[]>, expert: any) => {
      const name = expertDisplayName(expert);
      toDayList(expert.preferred_dates).forEach((key) => {
        acc[key] = [...(acc[key] || []), name];
      });
      return acc;
    },
    {}
  );

  const starredCount = Object.keys(preferredDaysByExpert).length;
  const starredLegend = (() => {
    if (starredCount === 0) return undefined;
    return starredCount === 1
      ? 'Expert’s preferred day'
      : 'Experts’ preferred days';
  })();

  const confirmStepOpen =
    savedDays.length > 0 && savedDays.length === savedDayCount;
  const daysWouldChange =
    savedDays.join(',') !== selectedDays.join(',') ||
    filmDays !== savedDayCount;
  const selectionCommitted = !!filmWindow?.locked;
  const dateConfirmedCount = optedInExperts.filter(hasConfirmedDate).length;
  // Changing the dates clears every confirmation and voids the sent SOWs —
  // whether the selection is already committed or still in Scheduling with
  // confirms/SOWs out. Warn in both cases.
  const needsRescheduleWarning =
    daysWouldChange &&
    savedDays.length > 0 &&
    (selectionCommitted || anyInternallyConfirmed || anySowLive);
  // Only an expert-less project is moved to Pre-Production by Save. With
  // experts, the selection moves itself once every confirmed expert's SOW is
  // signed and countersigned (the API rejects a manual push before that).
  const readyForPreProduction = scheduleComplete && !hasExperts;
  const wouldAdvanceStatus = readyForPreProduction && !selectionCommitted;
  const canSubmit = scheduleComplete && (daysWouldChange || wouldAdvanceStatus);

  const dayWord = filmDays === 1 ? 'day' : 'days';
  const awaitingExperts = optedInExperts.filter(
    (expert) => expert.user_confirmed == null
  );
  const declinedExperts = optedInExperts.filter(
    (expert) => expert.user_confirmed === false
  );
  const confirmableCount = optedInExperts.filter(
    (expert) => expert.internal_confirmed || expert.user_confirmed === true
  ).length;
  const isExpertConfirmable = (expert: any) =>
    confirmStepOpen &&
    !daysWouldChange &&
    !hasOptedOut(expert) &&
    (!!expert.internal_confirmed || expert.user_confirmed === true);
  const savedDayLabel = formatFilmDayList(savedDays);
  const viewDetailsHint = (() => {
    if (!daysWouldChange) return undefined;
    if (savedDays.length === 0) {
      return 'No filming dates are saved yet, so there is nothing to confirm against. Your unsaved picks will be lost.';
    }
    return `Answers for the saved ${savedDays.length > 1 ? 'dates' : 'date'}, ${savedDayLabel}. Your unsaved picks will be lost.`;
  })();
  // Scheduling acts on the whole selection, so the toast, the reschedule
  // warning and the save hint all have to name the projects it touches.
  const affectedProjects = ({ onSelection = false } = {}) =>
    sharedProcess.isShared
      ? `all ${sharedProcess.siblingProjects.length} projects${onSelection ? ' on this selection' : ''}`
      : 'this project';
  const pendingChangeBody = (() => {
    if (confirmableCount > 0) {
      return `${confirmableCount} expert${confirmableCount === 1 ? '' : 's'} already confirmed ${savedDayLabel}. Saving clears that and asks everyone to confirm the new ${dayWord}.`;
    }
    if (optedInExperts.length > 0) {
      return `The opted in experts were asked about ${savedDayLabel}. Saving asks them about the new ${dayWord} instead.`;
    }
    return 'No expert has opted in yet, so there is no one to ask until they do.';
  })();

  const confirmStepNotice = ((): ConfirmStepNoticeProps | null => {
    if (!hasExperts || loading) return null;
    if (!confirmStepOpen) {
      return {
        tone: 'info',
        title:
          filmDays > 1
            ? `Set all ${filmDays} filming days, then save`
            : 'Set the filming date, then save',
        body: 'Saving shares the schedule with the opted in experts and asks them to confirm their availability. You can confirm them here once they reply.',
      };
    }
    if (daysWouldChange) {
      if (!scheduleComplete) {
        return {
          tone: 'info',
          title: `Pick ${filmDays} filming ${dayWord}, then save`,
          body: `${selectedDays.length} of ${filmDays} selected. The experts keep their answer for ${savedDayLabel} until you save.`,
        };
      }
      return {
        tone: 'info',
        title: `Save to ask about ${formatFilmDayList(selectedDays)}`,
        body: pendingChangeBody,
      };
    }
    if (awaitingExperts.length > 0) {
      const names = awaitingExperts.map(expertDisplayName);
      return {
        tone: 'wait',
        title:
          names.length === 1
            ? `Waiting for ${names[0]} to confirm ${savedDayLabel}`
            : `Waiting for ${names.length} experts to confirm ${savedDayLabel}`,
        body:
          confirmableCount > 0
            ? 'You can confirm anyone who has already replied — confirming sends their SOW. The rest stay locked until they reply.'
            : 'Nothing to do here until they reply. The Scheduling status shows a green dot as soon as one of them confirms.',
        names: names.length > 1 ? names : undefined,
      };
    }
    if (confirmedCount > 0 && !selectionCommitted) {
      if (awaitingCountersign.length > 0) {
        const names = awaitingCountersign.map(expertDisplayName);
        return {
          tone: 'wait',
          title:
            names.length === 1
              ? `${names[0]} signed their SOW — countersign it`
              : `${names.length} SOWs are signed and need countersigning`,
          body: 'Countersign from the expert cards on the Expert Selection tab. The selection moves to Pre-Production once every confirmed expert\'s SOW is signed and countersigned.',
          names: names.length > 1 ? names : undefined,
        };
      }
      return {
        tone: 'wait',
        title:
          confirmedCount === 1
            ? 'Waiting for the expert to sign their SOW'
            : `${executedCount} of ${confirmedCount} SOWs fully signed`,
        body: 'Each confirmed expert was sent their Statement of Work. The selection moves to Pre-Production automatically once every one is signed and countersigned.',
      };
    }
    if (confirmableCount > 0) return null;
    if (declinedExperts.length > 0) {
      const names = declinedExperts.map(expertDisplayName);
      return {
        tone: 'blocked',
        title:
          names.length === 1
            ? `${names[0]} is not available on ${savedDayLabel}`
            : `${names.length} experts are not available on ${savedDayLabel}`,
        body: 'Pick a different filming date and save again to ask them once more.',
        names: names.length > 1 ? names : undefined,
      };
    }
    return {
      tone: 'blocked',
      title: 'No expert has opted in yet',
      body: 'Only experts who opted in to the filming range can confirm a date. Send opt-ins or a follow-up from Expert Proposals, then come back here.',
    };
  })();

  const saveHint = (() => {
    if (selectedDays.length === 0) {
      return `Pick ${filmDays === 1 ? 'a filming date' : `${filmDays} filming days`} and save to ask the experts to confirm.`;
    }
    if (!scheduleComplete) {
      return `Pick ${filmDays} ${dayWord}, ${selectedDays.length} selected so far.`;
    }
    if (hasExperts && daysWouldChange) {
      if (selectionCommitted) {
        return 'Save to reschedule: the selection returns to Scheduling, every confirmation is cleared and the sent SOWs are voided.';
      }
      return anyInternallyConfirmed || anySowLive
        ? 'Save to reschedule: every confirmation is cleared and the sent SOWs are voided; re-confirm each expert to send new ones.'
        : 'Save to ask the experts to confirm these dates.';
    }
    if (hasExperts && confirmedCount === 0) {
      if (confirmableCount > 0) {
        return 'Confirm an expert to send their SOW. The selection moves to Pre-Production once every confirmed expert\'s SOW is signed and countersigned.';
      }
      return awaitingExperts.length > 0
        ? `Waiting for ${awaitingExperts.length === 1 ? 'the expert' : `${awaitingExperts.length} experts`} to confirm ${savedDayLabel}.`
        : 'No expert has opted in yet, so none can be confirmed.';
    }
    if (hasExperts && !selectionCommitted) {
      return `${executedCount} of ${confirmedCount} SOW${confirmedCount === 1 ? '' : 's'} signed and countersigned. Pre-Production starts automatically when all are.`;
    }
    if (!canSubmit) {
      return `Filming on ${formatFilmDayList(selectedDays)}. Pick different ${dayWord} to reschedule.`;
    }
    return `Filming on ${formatFilmDayList(selectedDays)}.`;
  })();

  const mapExpert = (pro: any) => {
    const bpp = pro?.projects?.[0]?.BrandProjectPro;
    return {
      ...pro,
      internal_confirmed: bpp?.internal_confirmed || false,
      user_confirmed: bpp?.user_confirmed ?? null,
      preferred_dates: bpp?.preferred_dates ?? null,
      // Active SOW cycle (internal users only — the API omits these for brand
      // users, and brand users never reach this dialog).
      expert_signing_status: bpp?.expert_signing_status ?? null,
      internal_signing_status: bpp?.internal_signing_status ?? null,
      sow_on_file: !!bpp?.sow_on_file,
    };
  };

  const loadExperts = async ({ quiet = false } = {}) => {
    if (!hasExperts || !brandId || !project?.id) return;
    if (!quiet) setLoading(true);
    try {
      const res = await axiosInstant(getProjectPros(brandId, project.id));
      setExperts((res.data?.rows || []).map(mapExpert));
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  // Confirming SENDS the expert's SOW (the API awaits the send and reports
  // it) — or, with "SOW already signed" ticked, records it as on file and
  // runs the Pre-Production gate right away; un-confirming voids it.
  // Re-fetch afterwards so the SOW badges and the Expert Selection tab's
  // pills reflect the new cycle.
  const toggleConfirm = async (expert: any) => {
    const val = !expert.internal_confirmed;
    const sowOnFile = val && !!alreadySignedByUser[expert.userid];
    if (!val && expert.expert_signing_status === 'completed' && !pendingUnconfirm) {
      setPendingUnconfirm(expert);
      return;
    }
    setPendingUnconfirm(null);
    setTogglingUserId(expert.userid);
    try {
      const res = await axiosInstant.patch(
        `/brands/${brandId}/projects/${project.id}/pros/${expert.userid}/internal-confirm`,
        { internal_confirmed: val, sow_on_file: sowOnFile }
      );
      const sow = res.data?.sow || {};
      const name = expertDisplayName(expert);
      if (val && sow.on_file) {
        if (sow.recorded) {
          toast.success(
            `${name} confirmed — SOW recorded as already signed, nothing was sent.${
              sow.advanced ? ' The selection moved to Pre-Production.' : ''
            }`
          );
        } else {
          toast.success(`${name} confirmed — a SOW was already on record for them, nothing was sent.`);
        }
        setAlreadySignedByUser((prev) => ({ ...prev, [expert.userid]: false }));
      } else if (val) {
        if (sow.sent > 0) toast.success(`${name} confirmed — their SOW was sent for signature.`);
        else if (sow.failed > 0) toast.error(`${name} confirmed, but their SOW could not be sent. It will be retried overnight.`);
        else toast.success(`${name} confirmed.`);
      } else {
        toast.success(sow.superseded > 0 ? `${name} unconfirmed — their SOW was voided.` : `${name} unconfirmed.`);
      }
      setExperts((prev) => prev.map((e) => (e.userid === expert.userid ? { ...e, internal_confirmed: val } : e)));
      await loadExperts({ quiet: true }).catch(() => undefined);
      onComplete();
    } catch (error: any) {
      toast.error(FILM_DAYS_ERRORS[error?.response?.data?.error] || 'Failed to update expert confirmation.');
    } finally {
      setTogglingUserId(null);
    }
  };


  const savedMessage = () => {
    if (wouldAdvanceStatus) {
      return `Filming ${formatFilmDayList(selectedDays)}. Moved ${affectedProjects()} to Pre-Production.`;
    }
    // Nobody to ask on an expert-less project, so just confirm the dates.
    if (!hasExperts) return `Filming ${formatFilmDayList(selectedDays)}.`;
    if (needsRescheduleWarning) {
      return selectionCommitted
        ? 'Rescheduled. Selection moved back to Scheduling; confirmations cleared, SOWs voided, experts asked to confirm the new dates.'
        : 'Rescheduled. Confirmations cleared and sent SOWs voided; experts asked to confirm the new dates.';
    }
    return selectedDays.length > 1
      ? 'Dates saved. Experts asked to confirm.'
      : 'Date saved. Experts asked to confirm.';
  };

  const handleSaveClick = () => {
    if (!canSubmit) return;
    if (needsRescheduleWarning) {
      setShowRescheduleWarning(true);
      return;
    }
    handleSchedule();
  };

  const handleSchedule = async () => {
    if (!canSubmit) return;
    setShowRescheduleWarning(false);
    try {
      const body: Record<string, unknown> = {
        film_days: filmDays,
        film_dates: selectedDays,
      };
      if (readyForPreProduction) body.status = BRAND_PROJECT_STATUSES.PRE_PRODUCTION;
      const res = await axiosInstant.patch(`/brands/${brandId}/projects/${project.id}`, body);

      // The API echoes who the save notified; opted-in experts the client
      // didn't rank were told they weren't selected (once per expert).
      const notSelected = Number(res?.data?.film_date_notices?.notSelected) || 0;
      const notSelectedNote = notSelected > 0
        ? ` ${notSelected} unranked expert${notSelected === 1 ? ' was' : 's were'} told they weren't selected.`
        : '';
      toast.success(`${savedMessage()}${notSelectedNote}`);
      onComplete();
      onClose();
    } catch (error: any) {
      toast.error(FILM_DAYS_ERRORS[error?.response?.data?.error] || 'Failed to save.');
    }
  };

  useEffect(() => {
    loadExperts().catch(() => setLoading(false));
  }, [brandId, project?.id, hasExperts]); // eslint-disable-line

  if (pendingUnconfirm) {
    const name = expertDisplayName(pendingUnconfirm);
    const executed = pendingUnconfirm.internal_signing_status === 'completed';
    return (
      <Dialog open onClose={() => setPendingUnconfirm(null)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { maxWidth: 460, borderRadius: '20px' } }}>
        <DialogTitle className="flex justify-between items-center">
          <Typography variant="h5">Unconfirm {name}?</Typography>
          <IconButton onClick={() => setPendingUnconfirm(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent className="border-y border-lightgray3">
          <Typography sx={{ fontSize: 14, color: '#444', lineHeight: 1.7, pt: 2 }}>
            {name} has already <strong>signed</strong> their Statement of Work
            {executed ? ' and it is countersigned' : ''}. Unconfirming retires that
            contract; a signed copy cannot be voided at DocuSign, so it stays on
            record. Re-confirming later sends a brand new SOW.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button onClick={() => setPendingUnconfirm(null)} size="small"
            className="!py-0 !px-5 h-[44px] !rounded-xl !bg-white !text-dark1 !border !border-lightgray3 dark:!bg-surface-2 dark:!text-text-1 dark:!border-border-token">
            Keep confirmed
          </Button>
          <Button onClick={() => toggleConfirm(pendingUnconfirm)} size="small" className="!py-0 !px-5 h-[44px] !rounded-xl">
            Unconfirm
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (showRescheduleWarning) {
    return (
      <Dialog open onClose={() => setShowRescheduleWarning(false)} fullWidth maxWidth="xs"
        PaperProps={{ sx: { maxWidth: 460, borderRadius: '20px' } }}>
        <DialogTitle className="flex justify-between items-center">
          <Typography variant="h5">Reschedule this shoot?</Typography>
          <IconButton onClick={() => setShowRescheduleWarning(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent className="border-y border-lightgray3">
          <Typography sx={{ fontSize: 14, color: '#444', lineHeight: 1.7, pt: 2 }}>
            {selectionCommitted
              ? 'This selection is already committed, filming'
              : 'Experts are already confirmed for filming'}{' '}
            <strong>{formatFilmDayList(savedDays)}</strong>. Changing it to{' '}
            <strong>{formatFilmDayList(selectedDays) || 'no dates'}</strong> will:
          </Typography>
          <Box component="ul" sx={{ fontSize: 14, color: '#444', lineHeight: 1.8, pl: 2.5, mt: 1.5, mb: 1, listStyleType: 'disc' }}>
            {hasExperts && selectionCommitted && (
              <li>
                move {affectedProjects({ onSelection: true })} back to
                Scheduling until the experts confirm the new dates
              </li>
            )}
            <li>
              clear{' '}
              {dateConfirmedCount === 1
                ? '1 expert date confirmation'
                : `${dateConfirmedCount} expert date confirmations`}{' '}
              and every internal confirmation, and ask the experts to confirm the new dates
            </li>
            <li>void the sent SOWs (re-confirm each expert to send a new one)</li>
            {!hasExperts && sharedProcess.isShared && (
              <li>apply to {affectedProjects({ onSelection: true })}</li>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button onClick={() => setShowRescheduleWarning(false)} size="small"
            className="!py-0 !px-5 h-[44px] !rounded-xl !bg-white !text-dark1 !border !border-lightgray3 dark:!bg-surface-2 dark:!text-text-1 dark:!border-border-token">
            Keep current dates
          </Button>
          <Button onClick={handleSchedule} size="small" className="!py-0 !px-5 h-[44px] !rounded-xl">
            Reschedule
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxWidth: hasExperts ? 920 : 480, borderRadius: '20px' } }}>
      <DialogTitle className="flex justify-between items-center">
        <Typography variant="h5">
          {filmDays > 1 ? 'Filming Dates' : 'Filming Date'}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent className="border-y border-border-token !p-0">
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minHeight: { xs: 'auto', sm: filmDays > 1 ? 545 : 480 } }}>
          <Box sx={{ width: { xs: '100%', sm: hasExperts ? 340 : '100%' }, flexShrink: 0, borderRight: { xs: 'none', sm: hasExperts ? '1px solid var(--border)' : 'none' }, borderBottom: { xs: hasExperts ? '1px solid var(--border)' : 'none', sm: 'none' },
            display: 'flex', flexDirection: 'column', gap: 1.5, p: 2,
            '& .MuiDateCalendar-root': { width: '100% !important', maxWidth: '100% !important' },
            '& .MuiPickersLayout-root': { minWidth: 'unset !important' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography sx={sectionLabel}>
                {filmDays > 1 ? 'Select Filming Days' : 'Select Filming Date'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Typography sx={{ ...sectionLabel, letterSpacing: 0.5 }}>Days</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={filmDays}
                  onChange={(event) => changeFilmDays(Number(event.target.value))}
                  inputProps={{ min: 1, max: 30, style: { padding: '5px 6px', width: 40, textAlign: 'center' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              </Box>
            </Box>
            <ExpertCalendar
              bare
              label=""
              disabled={false}
              selectedDays={selectedDays}
              maxDays={filmDays}
              onToggleDay={toggleDay}
              minDate={toDay(filmWindow?.est_film_start) || undefined}
              maxDate={toDay(filmWindow?.est_film_end) || undefined}
              shouldDisableDate={
                hasFilmWindow(filmWindow)
                  ? (day: Dayjs) => !isWithinFilmWindow(day, filmWindow)
                  : undefined
              }
              placeholder={filmDays > 1 ? `Select ${filmDays} days` : 'Select a date'}
              starredDays={preferredDaysByExpert}
              caption={
                hasFilmWindow(filmWindow)
                  ? `Within ${formatFilmWindow(filmWindow)}`
                  : 'No filming range set on this selection yet.'
              }
              legend={starredLegend}
            />
            {filmDays > 1 && selectedDays.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selectedDays.map((day) => (
                  <Chip
                    key={day}
                    label={formatFilmDayList([day])}
                    size="small"
                    onDelete={() => toggleDay(day)}
                    sx={{
                      backgroundColor: '#F2EBE2', color: '#7A5C24', fontWeight: 700, fontSize: 12, height: 26,
                      '[data-theme="dark"] &': {
                        backgroundColor: 'var(--tint)',
                        color: 'var(--accent-text)',
                        '& .MuiChip-deleteIcon': { color: 'currentColor', opacity: 0.7 },
                      },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          {hasExperts && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ px: 3, pt: 2, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={sectionLabel}>Confirm Experts</Typography>
                <Chip label={`${confirmedCount} / ${optedInExperts.length}`} size="small"
                  sx={{ backgroundColor: confirmedCount > 0 ? 'var(--tint)' : 'var(--surface-2)', color: confirmedCount > 0 ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: 700, fontSize: 12, height: 26 }} />
              </Box>
              {sharedProcess.isShared && (
                <Typography sx={{ px: 3, pb: 1, fontSize: 12, color: 'var(--accent-text)' }}>
                  These experts are shared with {describeSharedSiblings(otherProcessProjects)},
                  so confirming applies to them too and sends the expert one SOW covering
                  every project — all of them move to Pre-Production together once every
                  confirmed expert&apos;s SOW is signed and countersigned.
                </Typography>
              )}
              {!loading && experts.length > 0 && (
                <Typography sx={{ px: 3, pb: 1, fontSize: 12, color: 'var(--text-2)' }}>
                  Only experts who opted in are shown
                  {optedInExperts.length < experts.length
                    ? ` (${optedInExperts.length} of ${experts.length} proposed).`
                    : '.'}
                </Typography>
              )}
              {confirmStepNotice && <ConfirmStepNotice {...confirmStepNotice} />}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  maxHeight: { xs: 'none', sm: filmDays > 1 ? 420 : 355 },
                  px: 2,
                  pb: 2,
                }}
              >
                {loading && <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'var(--text-2)' }}>Loading experts...</Typography>}
                {!loading && experts.length === 0 && <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'var(--text-2)' }}>No experts assigned.</Typography>}
                {!loading && experts.length > 0 && optedInExperts.length === 0 && <Typography variant="body2" sx={{ textAlign: 'center', py: 4, color: 'var(--text-2)' }}>No experts have opted in yet.</Typography>}
                {optedInExperts.map((expert) => (
                  <ExpertConfirmCard
                    key={expert.userid}
                    expert={expert}
                    isSelected={!!expert.internal_confirmed}
                    decision={getProDecision(getBpp(expert))}
                    onToggle={() => toggleConfirm(expert)}
                    preferredDates={expert.preferred_dates}
                    userConfirmed={expert.user_confirmed}
                    sowStatus={expert.expert_signing_status}
                    countersignStatus={expert.internal_signing_status}
                    sowOnFile={!!expert.sow_on_file}
                    alreadySignedChecked={!!alreadySignedByUser[expert.userid]}
                    onAlreadySignedChange={
                      isExpertConfirmable(expert)
                        ? (checked) => setAlreadySignedByUser((prev) => ({ ...prev, [expert.userid]: checked }))
                        : undefined
                    }
                    disabled={!isExpertConfirmable(expert) || togglingUserId === expert.userid}
                    onViewDetails={() => navigate(adminConfirmationPath(project.id, expert.userid))}
                    onDownloadDetails={canDownloadConfirmation(role) ? () => downloadConfirmationDetails(project.id, expert.userid) : undefined}
                    viewDetailsHint={viewDetailsHint}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid var(--border)' }}>
        <Typography sx={{ flex: 1, fontSize: 14, color: 'var(--text-2)' }}>
          {wouldAdvanceStatus ? (
            <>Saving moves {affectedProjects()} to Pre-Production.</>
          ) : (
            saveHint
          )}
        </Typography>
        <Button onClick={handleSaveClick} disabled={!canSubmit} size="small"
          className={classNames('!py-0 !px-5 h-[44px] !rounded-xl', { '!bg-tint !text-muted': !canSubmit })}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
